import { CalendarDaysIcon, ChartNoAxesCombinedIcon, KanbanSquareIcon, Table2Icon } from "lucide-react";
import type { ComponentProps } from "react";

import {
  ChannelAddMenuTrigger,
  ChannelNewSessionButton,
  ChannelSectionTabsView,
  channelSectionLinks,
} from "@product/app/workspace/channels/[channelId]/channel-section-tabs-view";
import { ChannelDataFreshnessIndicator } from "@product/app/workspace/channels/[channelId]/channel-data-freshness-indicator";
import {
  ChannelAccessButton,
  ChannelTraceDashboardButton,
  ChannelWorkspaceToolbarEndView,
  ChannelWorkspaceToolbarView,
} from "@product/app/workspace/channels/[channelId]/channel-workspace-toolbar-view";
import {
  ChannelViewActionsView,
  ChannelViewNavigationView,
  RecordAddFieldTrigger,
  RecordAddFilterTrigger,
  RecordColumnsTrigger,
  RecordFilterBarView,
  RecordNewRecordButton,
  RecordSortTrigger,
} from "@product/app/workspace/channels/[channelId]/record-filter-bar-view";
import {
  MobileRecordCardView,
  MobileRecordListView,
  RecordRowActionsView,
} from "@product/app/workspace/channels/[channelId]/record-table-view";
import { buildNewSessionHref } from "@product/lib/new-session-route";

import type { ChannelDataFreshness } from "@mlx/contracts";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { ShotRail } from "./chrome";
import type { ShotBoard } from "./channel-board";
import { ShotBoardView } from "./channel-board-view";
import type { ShotCalendar } from "./channel-calendar";
import { ShotCalendarView } from "./channel-calendar-view";
import type { ShotDashboard } from "./channel-dashboard";
import { ShotDashboardView } from "./channel-dashboard-view";
import { ShotCellValue, ShotChannelGrid } from "./channel-grid";
import type { ShotChannelOverview } from "./channel-overview";
import { ShotChannelOverviewView } from "./channel-overview-view";
import type { ShotChannelTable } from "./channel-table";
import { SHOT_WINDOW_HEIGHT_CLASS, ShotAppFrame } from "./window";
import type { ShotWorkspace } from "./workspace";

/**
 * The phone's record list (MobileRecordList at rest): a card per record with
 * its row actions and its first six fields, each in the list's `pb-2` row.
 */
function ShotRecordCards({ table }: { table: ShotChannelTable }) {
  const previewFields = table.fields.filter((field) => field.type !== "json" && field.type !== "file").slice(0, 6);
  return (
    <MobileRecordListView>
      {table.rows.map((row) => (
        <div key={row.id} className="pb-2">
          <MobileRecordCardView
            title={row.title}
            actions={<RecordRowActionsView record={row} />}
            fields={previewFields.map((field) => ({
              key: field.key,
              label: field.label,
              value: <ShotCellValue cell={row.cells[field.key]!} />,
            }))}
          />
        </div>
      ))}
    </MobileRecordListView>
  );
}

/**
 * The data set's freshness as the header's indicator reads it: kept from its
 * sources by the connectors, within its freshness target. At rest only the
 * status shows, as the indicator's dot.
 */
const SHOT_DATA_FRESHNESS: ChannelDataFreshness = {
  kind: "exact_connector",
  status: "fresh",
  generatedAt: 0,
  dataAsOf: null,
  ageMs: null,
  maxStalenessMs: null,
  lastIngestionAt: null,
  lastProjectionRefreshAt: null,
  nextScheduledAt: null,
  nextRetryAt: null,
  snapshotCapturedAt: null,
  snapshotExpiresAt: null,
  desiredProjectionWatermark: null,
  servedProjectionWatermark: null,
  sourceSystems: [],
  sourceWatermarks: [],
  reasonCodes: ["within_target"],
  materialized: true,
};

/** A view kind's picker icon and label (dataViewTypes in channel-view-options.ts). */
const VIEW_KINDS = {
  table: { icon: Table2Icon, label: "Table" },
  board: { icon: KanbanSquareIcon, label: "Board" },
  calendar: { icon: CalendarDaysIcon, label: "Calendar" },
  dashboard: { icon: ChartNoAxesCombinedIcon, label: "Dashboard" },
} as const;

/**
 * What a channel shot can show: the channel's Overview, and the views of its
 * data set; the table is always there.
 */
export type ShotChannelViews = {
  table: ShotChannelTable;
  board?: ShotBoard;
  calendar?: ShotCalendar;
  dashboard?: ShotDashboard;
  overview?: ShotChannelOverview;
};

/** A saved view of the data set. */
type DataView = keyof typeof VIEW_KINDS;

export type ShotChannelView = DataView | "overview";

/** The filter bar's actions for a view kind, as RecordFilterBar shows them at rest. */
function FilterActions({ view, table }: { view: DataView; table: ShotChannelTable }) {
  if (view === "dashboard" || view === "calendar") {
    return (
      <>
        <RecordAddFieldTrigger />
        <RecordNewRecordButton />
      </>
    );
  }
  if (view === "board") {
    return (
      <>
        <RecordAddFieldTrigger />
        <RecordSortTrigger />
        <RecordNewRecordButton />
      </>
    );
  }
  return (
    <>
      <RecordAddFieldTrigger />
      <RecordColumnsTrigger viewName={table.viewName} visibleCount={table.fields.length} fieldCount={table.fields.length} />
      <RecordSortTrigger fieldLabel={table.sortField.label} active />
      <RecordNewRecordButton />
    </>
  );
}

/** The filter bar over a saved view: the view picker, Add filter and the view's actions. */
function ShotDataViewBar({ view, views }: { view: DataView; views: ShotChannelViews }) {
  const { table, board, calendar, dashboard } = views;
  const viewName = { table: table.viewName, board: board?.viewName, calendar: calendar?.viewName, dashboard: dashboard?.viewName }[view];
  if (!viewName) throw new Error(`The channel shot has no ${view} to show.`);
  const kind = VIEW_KINDS[view];
  return (
    <RecordFilterBarView
      navigation={<ChannelViewNavigationView viewName={viewName} icon={kind.icon} title={`${viewName} (${kind.label})`} />}
      filters={
        <>
          {/* The reui filters' wrapper around their closed "Add filter". */}
          <div className="flex flex-wrap items-center gap-1.5">
            <RecordAddFilterTrigger />
          </div>
          <ChannelViewActionsView selectedView={{ name: viewName }} />
        </>
      }
      actions={<FilterActions view={view} table={table} />}
    />
  );
}

/**
 * A channel with a data set open in one of its saved views, in the
 * application frame: the channel's title row, its section tabs on Data, the
 * filter bar with the view, its filters and actions, then the view: the
 * table's grid (from `sm`) or cards on a phone, the board, the calendar or
 * the dashboard. On the Overview tab, the channel's Overview instead.
 * Everything inside is the product's own views over the story's records;
 * the wrappers between them are the channel surface's
 * (channel-workspace-surface.tsx), with its knowledge split at rest, the
 * content panel alone.
 */
export function ShotChannelWindow({
  tone = "light",
  workspace,
  table,
  board,
  calendar,
  dashboard,
  overview,
  view = "table",
  className,
  heightClass = SHOT_WINDOW_HEIGHT_CLASS,
  ...props
}: {
  tone?: AppThemeTone;
  workspace: ShotWorkspace;
  view?: ShotChannelView;
  className?: string;
  heightClass?: string;
} & ShotChannelViews &
  Omit<ComponentProps<"div">, "children">) {
  const { channel } = table;
  const channelHref = `/workspace/channels/${encodeURIComponent(channel.id)}`;
  const newSessionHref = buildNewSessionHref({ channelId: channel.id, returnTo: channelHref });
  if (view === "overview" && !overview) throw new Error("The channel shot has no overview to show.");
  return (
    <ShotAppFrame
      tone={tone}
      workspace={workspace}
      rail={<ShotRail workspace={workspace} tab="channels" activeChannelId={channel.id} />}
      className={className}
      heightClass={heightClass}
      {...props}
    >
      {/* The workspace shell's page content (workspace-navigation-shell). */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <section className="channel-workspace-container flex h-full min-h-0 flex-col bg-background [--channel-canvas:hsl(var(--background))]">
          <ChannelWorkspaceToolbarView
            channel={channel}
            // The header names the data set only inside one.
            dataSet={view === "overview" ? null : { name: table.dataSetName }}
            end={
              <ChannelWorkspaceToolbarEndView
                // Inside a data set kept from its sources, its freshness.
                freshness={view === "overview" ? undefined : <ChannelDataFreshnessIndicator freshness={SHOT_DATA_FRESHNESS} />}
                actions={
                  <>
                    {view === "dashboard" ? <ChannelTraceDashboardButton /> : null}
                    <div className="size-7 shrink-0">
                      <ChannelAccessButton />
                    </div>
                  </>
                }
              />
            }
          />
          <ChannelSectionTabsView
            links={channelSectionLinks(channel.id)}
            selectedView={view === "overview" ? "overview" : "data"}
            compactActions={
              <>
                <ChannelAddMenuTrigger />
                <ChannelNewSessionButton compact href={newSessionHref} />
              </>
            }
            expandedActions={
              <>
                <ChannelAddMenuTrigger />
                <ChannelNewSessionButton href={newSessionHref} />
              </>
            }
          />
          <div className="flex h-full min-h-0 w-full flex-1">
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="relative h-full min-h-0">
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  {view === "overview" ? null : (
                    <ShotDataViewBar view={view} views={{ table, board, calendar, dashboard }} />
                  )}
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {view === "overview" && overview ? (
                      <ShotChannelOverviewView overview={overview} />
                    ) : view === "board" && board ? (
                      <ShotBoardView board={board} />
                    ) : view === "calendar" && calendar ? (
                      <ShotCalendarView calendar={calendar} />
                    ) : view === "dashboard" && dashboard ? (
                      <ShotDashboardView dashboard={dashboard} />
                    ) : (
                      <section className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                        <ShotRecordCards table={table} />
                        <div className="hidden min-h-0 flex-1 sm:flex">
                          <ShotChannelGrid table={table} />
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </ShotAppFrame>
  );
}
